import type { AcpPlanData, CrowdexUIMessage } from '@crowdex/agent-contracts';
import { useCallback, useEffect, useMemo } from 'react';
import type { AgentTerminalDisplay } from '@/components/desktop-panel-primitives';
import { useBackgroundTerminalReconciliation } from '@/hooks/use-background-terminal-reconciliation';
import type { BackgroundTerminalCompletion } from '@/lib/conversations/message-part/terminal';
import { conversationRefs as deriveConversationRefs } from '@/lib/conversations/tool-refs';
import { PREVIEW_MESSAGES } from '@/lib/desktop-preview-data';
import { previewBoard, previewBoardName } from '@/lib/desktop-preview-transcript';
import {
  conversationSources as deriveConversationSources,
  conversationSubagents as deriveConversationSubagents,
  messageAgentTerminals as deriveMessageAgentTerminals,
  resolveDisplayedPlan,
  selectedSubagentData,
  stopAgentTerminalParts,
} from '@/lib/desktop-shell/message-derivations';

interface ConversationArtifactsOptions {
  messages: CrowdexUIMessage[];
  setMessages: (
    messages: CrowdexUIMessage[] | ((current: CrowdexUIMessage[]) => CrowdexUIMessage[]),
  ) => void;
  status: string;
  previewConversation: boolean;
  selectedSubagentId: string | null;
}

/** Derives transcript artifacts and coordinates their side-panel lifecycle. */
export function useConversationArtifacts({
  messages,
  setMessages,
  status,
  previewConversation,
  selectedSubagentId,
}: ConversationArtifactsOptions) {
  const displayedPlan = useMemo<AcpPlanData | null>(
    () => resolveDisplayedPlan(messages),
    [messages],
  );

  const selectedSubagent = useMemo(
    () => selectedSubagentData(messages, selectedSubagentId),
    [messages, selectedSubagentId],
  );
  const conversationSubagents = useMemo(() => deriveConversationSubagents(messages), [messages]);
  const visibleAgentTerminals = useMemo<readonly AgentTerminalDisplay[]>(
    () => deriveMessageAgentTerminals(messages),
    [messages],
  );
  const markAgentTerminalStopped = useCallback(
    (terminalId: string, completion?: BackgroundTerminalCompletion) => {
      setMessages((current) => stopAgentTerminalParts(current, terminalId, completion));
    },
    [setMessages],
  );
  useBackgroundTerminalReconciliation(visibleAgentTerminals, markAgentTerminalStopped);
  const conversationSources = useMemo(() => deriveConversationSources(messages), [messages]);
  // What its tools touched, for its Summary (derived, as its sources are).
  const conversationRefs = useMemo(
    () => deriveConversationRefs(messages).map((entry) => entry.ref),
    [messages],
  );

  useEffect(() => {
    // `?board=` shows one of the redesign's transcript boards instead.
    if (previewConversation)
      setMessages(previewBoard(previewBoardName(window.location))?.messages ?? PREVIEW_MESSAGES);
  }, [previewConversation, setMessages]);

  const busy = status === 'submitted' || status === 'streaming';

  return {
    busy,
    conversationRefs,
    conversationSources,
    conversationSubagents,
    displayedPlan,
    markAgentTerminalStopped,
    selectedSubagent,
    visibleAgentTerminals,
  };
}
